import React from "react";
import { Palette } from "../holidayConfig";
import { mix, snowCap } from "./kit";
import { Pumpkin } from "./HalloweenCharacters";
import { range, seeded } from "./sceneUtils";
import { useToonId } from "./toon";

/*
 * Borgerskapets änkehus på Norrtullsgatan 45 (Langlet & Söderberg, 1879),
 * där kontoret ligger. Den laxrosa putsfasaden, mittpartiet med de höga
 * rundbågefönstren, vindsvåningen från 1927, det svarta klocktornet med
 * "1879" på sockeln och den inhuggna skylten BORGERSKAPETS ENKEHUS över
 * porten.
 *
 * Två stämningar:
 *  - halloween: månljus, porten på glänt, lyktgubbar på grindstolparna,
 *    änkan i fönstret och fladdermöss runt tornet.
 *  - lucia / jul: blå timme en decembermorgon, eller julnatt under
 *    mörkröd himmel. Snö på taket, gesimserna och
 *    fönsterblecken, adventsljusstakar och pappersstjärnor i fönstren,
 *    krans på porten, krukgranar med ljusslingor vid trappan (som i
 *    verkligheten) och marschaller på trottoaren.
 *
 * Koordinaterna utgår från trappans mitt (0, 0) och y växer uppåt som
 * negativa tal. `p` styr fönsterljusets färg.
 */

/** Topp på klocktornets spira (y i husets koordinater). Blixten på Halloween slår ner här. */
export const ENKEHUS_SPIRE_TOP = -262;

export type EnkehusMood = "halloween" | "lucia" | "jul";

const SALMON = "#e0906f";

interface Tones {
  night: string;
  wall: string;
  wallProud: string;
  groove: string;
  trim: string;
  shadow: string;
  frame: string;
  stone: string;
  stoneDark: string;
  slate: string;
  slateLit: string;
  glass: string;
  wood: string;
  iron: string;
  cornice: string;
  /** Snö (bara vinterlägena). */
  snow?: string;
  snowShade?: string;
}

const NIGHTS: Record<EnkehusMood, string> = { halloween: "#170904", lucia: "#141a4a", jul: "#2a0a14" };

const tones = (mood: EnkehusMood): Tones => {
  // Halloween: varm, nästan svart natt. Lucia och jul: en ljusare vinterkväll
  // där putsen fortfarande syns tydligt mot snön.
  const night = NIGHTS[mood];
  const winter = mood !== "halloween";
  const k = winter ? -0.1 : 0;
  const n = (c: string, t: number) => mix(c, night, Math.max(0, t + k));
  const pick = (h: string, l: string, j: string) => (mood === "halloween" ? h : mood === "lucia" ? l : j);
  return {
    night,
    wall: n(SALMON, 0.44),
    wallProud: n(SALMON, 0.39),
    groove: n(SALMON, 0.72),
    trim: n("#f0bea4", 0.38),
    shadow: n(SALMON, 0.8),
    frame: n("#7d938c", 0.5),
    stone: n("#9a948d", 0.5),
    stoneDark: n("#9a948d", 0.72),
    slate: pick("#1a1412", "#161a33", "#22141a"),
    slateLit: pick("#3a302c", "#343a5c", "#46343c"),
    glass: pick("#130805", "#0c0f2c", "#16080e"),
    wood: n("#8a5230", 0.45),
    iron: pick("#0a0402", "#0a0c1f", "#0e0508"),
    cornice: n("#e8a888", 0.46),
    snow: winter ? "#eef1ff" : undefined,
    snowShade: winter ? "#aab3e0" : undefined,
  };
};

/** Mittpartiets halva bredd, flygelaxlarnas avstånd och antal. */
const RIS = 60;
const AX = 22;
const LEFT_AXES = 4;
const RIGHT_AXES = 4;
const L = -RIS - LEFT_AXES * AX;
const R = RIS + RIGHT_AXES * AX;

/** Våningarna (överkant), nerifrån och upp. */
const Y = {
  socle: -8,
  ground: -52,
  band: -56,
  f1: -82,
  f2: -108,
  f3: -134,
  dentil: -142,
  attic: -164,
  eave: -168,
  roof: -182,
};

type Shape = "rect" | "arch" | "seg";

const winPath = (x: number, top: number, w: number, h: number, shape: Shape) => {
  const l = x - w / 2;
  const r = x + w / 2;
  const b = top + h;
  if (shape === "arch") {
    const rr = w / 2;
    return `M${l} ${b} V${top + rr} A${rr} ${rr} 0 0 1 ${r} ${top + rr} V${b} Z`;
  }
  if (shape === "seg") return `M${l} ${b} V${top + 2.2} Q${x} ${top - 1.6} ${r} ${top + 2.2} V${b} Z`;
  return `M${l} ${top} H${r} V${b} H${l} Z`;
};

interface WinProps {
  t: Tones;
  x: number;
  top: number;
  w: number;
  h: number;
  shape: Shape;
  lit: boolean;
  flicker?: boolean;
  delay?: number;
  /** Rundbågefönstren i mittpartiet har ett cirkelformat överljus. */
  oculus?: boolean;
  keystone?: boolean;
  gradient: string;
  glowColor: string;
  children?: React.ReactNode;
}

const Win: React.FC<WinProps> = ({ t, x, top, w, h, shape, lit, flicker, delay = 0, oculus, keystone, gradient, glowColor, children }) => {
  const d = winPath(x, top, w, h, shape);
  const spring = shape === "arch" ? top + w / 2 : top + 2;
  const b = top + h;
  const glass = (
    <>
      <path d={d} fill={lit ? `url(#${gradient})` : t.glass} />
      {!lit && <path d={`M${x - w / 2} ${top + h * 0.55} L${x + w * 0.1} ${top} H${x + w * 0.35} L${x - w / 2} ${top + h * 0.85} Z`} fill={t.trim} opacity="0.1" />}
      {children}
    </>
  );
  return (
    <g>
      {lit && <path d={winPath(x, top - 4, w + 8, h + 8, shape)} fill={glowColor} opacity="0.3" filter="url(#toon-soft)" />}
      <path d={winPath(x, top - 1.3, w + 2.6, h + 2.6, shape)} fill={t.frame} />
      {lit && flicker ? (
        <g className="hol-flicker" style={{ animationDelay: `${delay}s` }}>
          {glass}
        </g>
      ) : (
        glass
      )}
      <g stroke={t.frame} strokeWidth={w > 14 ? 1.3 : 1} fill="none">
        {oculus ? (
          <>
            <path d={`M${x} ${b} V${spring + w * 0.28}`} />
            <circle cx={x} cy={spring - 0.4} r={w * 0.26} />
            <path d={`M${x - w / 2} ${spring + w * 0.28} H${x + w / 2}`} />
          </>
        ) : (
          <>
            <path d={`M${x} ${b} V${spring + (shape === "arch" ? 0 : h * 0.3)}`} />
            <path d={`M${x - w / 2} ${spring + h * 0.3} H${x + w / 2}`} />
          </>
        )}
      </g>
      {keystone && <path d={`M${x - 2.4} ${top - 3.2} H${x + 2.4} L${x + 1.6} ${top + 1.2} H${x - 1.6} Z`} fill={t.trim} opacity="0.85" />}
      <rect x={x - w / 2 - 2} y={b + 1.2} width={w + 4} height={1.8} fill={t.trim} opacity="0.7" />
      {t.snow && <path d={snowCap(x - w / 2 - 2, x + w / 2 + 2, b + 1, 1.2, Math.round(x * 7 + top))} fill={t.snow} />}
    </g>
  );
};

/** Adventsljusstake (sju ljus i en trappa) som silhuett i fönstret. (x, y) = fotens mitt. */
const CandleBridge: React.FC<{ x: number; y: number; w: number }> = ({ x, y, w }) => {
  const h = w * 0.42;
  return (
    <g>
      <path d={`M${x - w / 2} ${y} L${x} ${y - h} L${x + w / 2} ${y} Z`} fill="#3a2410" opacity="0.85" />
      {range(7).map(i => {
        const u = i / 6;
        const cx = x - w / 2 + u * w;
        const cy = y - (1 - Math.abs(u - 0.5) * 2) * h - 1;
        return <circle key={i} cx={cx} cy={cy} r={0.75} fill="#ffffff" />;
      })}
    </g>
  );
};

/** Adventsstjärna i papper som hänger i fönstret. */
const WindowStar: React.FC<{ x: number; y: number; r: number }> = ({ x, y, r }) => (
  <g>
    <path d={`M${x} ${y - r * 2.2} V${y - r}`} stroke="#5a3a18" strokeWidth="0.4" />
    <circle cx={x} cy={y} r={r * 1.8} fill="#ffffff" opacity="0.35" filter="url(#toon-soft)" />
    <polygon
      points={range(10)
        .map(q => {
          const rr = q % 2 ? r * 0.45 : r;
          const a = (Math.PI / 5) * q - Math.PI / 2;
          return `${(x + Math.cos(a) * rr).toFixed(2)},${(y + Math.sin(a) * rr).toFixed(2)}`;
        })
        .join(" ")}
      fill="#fffaf0"
    />
  </g>
);

/** Liten fladdermus i siluett som cirklar kring tornet. */
const TowerBat: React.FC<{ path: string; dur: number; begin: number; s?: number }> = ({ path, dur, begin, s = 1 }) => (
  <g>
    <animateMotion dur={`${dur}s`} begin={`${begin}s`} repeatCount="indefinite" path={path} rotate="0" />
    <g transform={`scale(${s})`}>
      <path
        d="M0 0 C -3 -3, -7 -3.4, -10 -1 C -8 -1, -7 1, -7 2.4 C -5 0.4, -2.6 1.2, 0 2.6 C 2.6 1.2, 5 0.4, 7 2.4 C 7 1, 8 -1, 10 -1 C 7 -3.4, 3 -3, 0 0 Z"
        fill="#0b0402"
      >
        <animateTransform attributeName="transform" type="scale" values="1 1; 1 -0.5; 1 1" dur="0.34s" repeatCount="indefinite" />
      </path>
    </g>
  </g>
);

/** Änkan i fönstret: siluett med knut i nacken och ett tänt ljus. */
const Widow: React.FC<{ x: number; top: number; p: Palette }> = ({ x, top, p }) => (
  <g>
    <circle cx={x + 3.4} cy={top + 10.4} r="3" fill={p[100]} opacity="0.7" filter="url(#toon-soft)" className="hol-breathe" />
    <path d={`M${x - 5.5} ${top + 18} Q ${x - 5.5} ${top + 10.2} ${x - 1.2} ${top + 10.2} Q ${x + 2.6} ${top + 10.2} ${x + 2.8} ${top + 18} Z`} fill="#1f0d06" />
    <circle cx={x - 1.3} cy={top + 6.8} r="2.5" fill="#1f0d06" />
    <circle cx={x - 3.6} cy={top + 5.6} r="1.4" fill="#1f0d06" />
    <rect x={x + 3} y={top + 11.6} width="1" height="3" fill={p[50]} />
    <ellipse cx={x + 3.5} cy={top + 10.6} rx="0.7" ry="1.3" fill="#ffffff" />
  </g>
);

/** Krukgran med snö och ljusslinga. (x, y) = krukans fot. */
const PottedSpruce: React.FC<{ x: number; y: number; t: Tones; delay: number }> = ({ x, y, t, delay }) => {
  const tiers = [
    [0, 9, 8],
    [-6, 7.4, 8],
    [-11.5, 5.6, 7],
    [-16.5, 3.6, 6],
  ];
  const lights: [number, number][] = [
    [-6, -11],
    [3, -12.5],
    [-2.6, -17],
    [4.6, -19],
    [-3.2, -23],
    [2, -26],
    [0, -29.5],
  ];
  return (
    <g transform={`translate(${x} ${y})`}>
      <ellipse cx="0" cy="0" rx="9" ry="1.6" fill="#000000" opacity="0.3" />
      <path d="M-5.5 0 L-6.6 -8 H6.6 L5.5 0 Z" fill={mix("#6a6a72", t.night, 0.4)} />
      <rect x="-7.2" y="-9.2" width="14.4" height="1.8" rx="0.6" fill={mix("#8a8a92", t.night, 0.35)} />
      <path d="M-6.4 -9.2 Q0 -11.4 6.4 -9.2" stroke={t.snow} strokeWidth="1.4" fill="none" />
      {tiers.map(([ty, w, h], i) => (
        <g key={i}>
          <path d={`M${-w} ${ty - 9} L0 ${ty - 9 - h * 1.6} L${w} ${ty - 9} Z`} fill={mix("#1c3b2c", t.night, 0.3)} />
          <path d={`M${-w} ${ty - 9} L0 ${ty - 9 - h * 1.6} L${-w * 0.1} ${ty - 9} Z`} fill={mix("#0e2219", t.night, 0.3)} opacity="0.5" />
          <path d={`M${-w * 0.95} ${ty - 9.6} Q${-w * 0.3} ${ty - 12.6} ${w * 0.5} ${ty - 10.6} Q${w * 0.2} ${ty - 9.2} ${-w * 0.95} ${ty - 9.6} Z`} fill={t.snow} opacity="0.9" />
        </g>
      ))}
      {lights.map(([lx, ly], i) => (
        <g key={i} className="hol-twinkle" style={{ animationDelay: `${delay - i * 0.45}s` }}>
          <circle cx={lx} cy={ly} r="2.4" fill="#ffd27a" opacity="0.35" filter="url(#toon-soft)" />
          <circle cx={lx} cy={ly} r="0.75" fill="#fff6d8" />
        </g>
      ))}
    </g>
  );
};

/** Marschall i snön med fladdrande låga. (x, y) = foten. */
const Marschall: React.FC<{ x: number; y: number; delay: number }> = ({ x, y, delay }) => (
  <g transform={`translate(${x} ${y})`}>
    <circle cx="0" cy="-12" r="16" fill="#ffb347" opacity="0.28" filter="url(#toon-softer)" className="hol-flicker" style={{ animationDelay: `${delay}s` }} />
    <ellipse cx="0" cy="0" rx="10" ry="2" fill="#ffb347" opacity="0.3" filter="url(#toon-soft)" />
    <path d="M-2.4 0 L-2.8 -8 H2.8 L2.4 0 Z" fill="#2a1f1a" />
    <rect x="-3" y="-8.6" width="6" height="1.2" fill="#4a3a30" />
    <g className="hol-candle-flame" style={{ animationDelay: `${delay}s` }}>
      <path d="M0 -17 C 3.6 -12.6, 3.4 -9.6, 0 -8.6 C -3.4 -9.6, -3.6 -12.6, 0 -17 Z" fill="url(#lu-flame)" />
      <path d="M0 -12.8 C 1.2 -11.2, 1.1 -9.8, 0 -9.4 C -1.1 -9.8, -1.2 -11.2, 0 -12.8 Z" fill="#ffffff" />
    </g>
  </g>
);

const Enkehus: React.FC<{ x: number; y: number; s?: number; p: Palette; mood?: EnkehusMood }> = ({ x, y, s = 1, p, mood = "halloween" }) => {
  const id = useToonId("ek");
  const t = tones(mood);
  const winter = mood !== "halloween";
  const rnd = seeded(1879);
  const litGrad = `${id}-lit`;
  const doorGrad = `${id}-door`;
  const glowColor = p[400];
  const wingX = [
    ...range(LEFT_AXES).map(i => -RIS - AX / 2 - i * AX),
    ...range(RIGHT_AXES).map(i => RIS + AX / 2 + i * AX),
  ];
  const windowProps = { t, gradient: litGrad, glowColor };
  // En vintermorgon eller julnatt är fler fönster tända.
  const lit = (chance: number) => rnd() < (winter ? Math.min(0.85, chance * 2) : chance);
  const WIDOW_X = -RIS - AX / 2 - 2 * AX;

  /** Adventsljusstake eller stjärna i ett tänt fönster (bara vinterlägena). */
  const deco = (wx: number, top: number, w: number, h: number, k: number) => {
    if (!winter) return null;
    return k % 3 === 1 ? <WindowStar x={wx} y={top + h * 0.36} r={w * 0.26} /> : <CandleBridge x={wx} y={top + h - 0.6} w={w * 0.82} />;
  };

  // Halloween och jul: fem i tolv. Lucia: fem i åtta, innan solen gått upp.
  const clock = mood === "lucia" ? { hour: 237.5, minute: 330 } : { hour: 357.5, minute: 330 };
  const hand = (deg: number, len: number) => {
    const a = ((deg - 90) * Math.PI) / 180;
    return `M0 ${-199} l${Math.cos(a) * len} ${Math.sin(a) * len}`;
  };

  const towerBatPath = "M0 -236 C 34 -236, 46 -226, 40 -214 C 30 -198, -30 -198, -40 -214 C -46 -226, -34 -236, 0 -236 Z";

  return (
    <g transform={`translate(${x} ${y}) scale(${s})`}>
      <defs>
        <radialGradient id={litGrad} cx="50%" cy="70%" r="75%">
          <stop offset="0%" stopColor={p[100]} />
          <stop offset="55%" stopColor={p[300]} />
          <stop offset="100%" stopColor={p[500]} />
        </radialGradient>
        <radialGradient id={doorGrad} cx="50%" cy="80%" r="80%">
          <stop offset="0%" stopColor={p[50]} />
          <stop offset="50%" stopColor={p[200]} />
          <stop offset="100%" stopColor={p[600]} />
        </radialGradient>
        <linearGradient id={`${id}-shade`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={t.night} stopOpacity={winter ? 0.3 : 0.45} />
          <stop offset="45%" stopColor={t.night} stopOpacity="0" />
          <stop offset="78%" stopColor={p[500]} stopOpacity="0" />
          <stop offset="100%" stopColor={p[500]} stopOpacity={winter ? 0.16 : 0.22} />
        </linearGradient>
        <linearGradient id={`${id}-roofsnow`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="100%" stopColor={t.snowShade ?? "#ffffff"} />
        </linearGradient>
      </defs>

      {/* Trottoaren och gången från porten, upplysta av porten */}
      <rect x={L - 16} y="0" width={R - L + 32} height="12" fill={winter ? mix("#c9d0f2", t.night, 0.25) : mix("#6f6660", t.night, 0.62)} />
      {!winter && <path d="M-18 0 H18 L22 12 H-22 Z" fill={mix("#8d8984", t.night, 0.5)} />}
      <ellipse cx="0" cy="4" rx="70" ry="10" fill={p[400]} opacity={winter ? 0.3 : 0.22} filter="url(#toon-softer)" />

      {/* Taket med skorstenar och oxögon */}
      <path d={`M${L - 2} ${Y.eave} L${L + 12} ${Y.roof} H${R - 12} L${R + 2} ${Y.eave} Z`} fill={t.slate} />
      <path d={`M${R - 12} ${Y.roof} L${R + 2} ${Y.eave} H${R - 6} Z`} fill={t.slateLit} opacity="0.6" />
      <path d={`M${L + 12} ${Y.roof} H${R - 12}`} stroke={t.slateLit} strokeWidth="1.2" />
      {winter && (
        <>
          {/* Snötäcket på plåttaket, med en kant som hänger över takfoten */}
          <path d={`M${L - 1} ${Y.eave - 1.5} L${L + 12.5} ${Y.roof - 1} H${R - 12.5} L${R + 1} ${Y.eave - 1.5} Z`} fill={`url(#${id}-roofsnow)`} />
          <path d={snowCap(L - 3, R + 3, Y.eave - 1.2, 2.6, 13)} fill={t.snow} />
          {range(Math.floor((R - L) / 16)).map(i => (
            <path key={i} d={`M${L + 14 + i * 16} ${Y.roof + 2} l-1.4 ${Y.eave - Y.roof - 4}`} stroke={t.snowShade} strokeWidth="0.6" opacity="0.5" />
          ))}
        </>
      )}
      {[-118, -78, 82, 122].map((cx, i) => (
        <g key={cx}>
          {winter &&
            range(3).map(k => (
              <circle
                key={k}
                className="hol-smoke"
                cx={cx}
                cy={Y.roof - 14}
                r={3.4}
                fill="#c9cff0"
                opacity="0.35"
                style={{ animationDelay: `${-k * 2.4 - i * 1.3}s` }}
              />
            ))}
          <rect x={cx - 4} y={Y.roof - 11} width="8" height="12" fill={t.slate} />
          <rect x={cx - 5} y={Y.roof - 12.5} width="10" height="2.4" fill={t.slateLit} />
          {winter && <path d={snowCap(cx - 5.4, cx + 5.4, Y.roof - 12.8, 1.8, cx)} fill={t.snow} />}
        </g>
      ))}
      {[-98, 104].map((cx, i) => (
        <g key={cx}>
          <path d={`M${cx - 8} ${Y.eave} V${Y.eave - 5} A8 8 0 0 1 ${cx + 8} ${Y.eave - 5} V${Y.eave} Z`} fill={t.slateLit} />
          <ellipse cx={cx} cy={Y.eave - 5.5} rx="4.4" ry="3.6" fill={i === 1 || winter ? p[300] : t.glass} />
          <path d={`M${cx - 4.4} ${Y.eave - 5.5} H${cx + 4.4} M${cx} ${Y.eave - 9} V${Y.eave - 2}`} stroke={t.frame} strokeWidth="0.8" />
          {winter && <path d={`M${cx - 8.6} ${Y.eave - 5} A8.6 8.6 0 0 1 ${cx + 8.6} ${Y.eave - 5} Q${cx + 6} ${Y.eave - 9.4} ${cx} ${Y.eave - 10.6} Q${cx - 6} ${Y.eave - 9.4} ${cx - 8.6} ${Y.eave - 5} Z`} fill={t.snow} />}
        </g>
      ))}

      {/* Fasaden */}
      <rect x={L} y={Y.eave} width={R - L} height={-Y.eave} fill={t.wall} />
      <rect x={L} y={Y.socle} width={R - L} height={-Y.socle} fill={t.stone} />
      <rect x={L} y={Y.socle} width={R - L} height="1.4" fill={t.stoneDark} />
      {/* Mittpartiet skjuter fram en aning */}
      <rect x={-RIS} y={Y.eave - 2} width={RIS * 2} height={-Y.eave + 2} fill={t.wallProud} />
      <rect x={-RIS - 5} y={Y.eave} width="5" height={-Y.eave} fill={t.shadow} opacity="0.4" />
      <rect x={RIS - 2.2} y={Y.eave - 2} width="2.2" height={-Y.eave + 2} fill={t.trim} opacity="0.35" />
      <rect x={-RIS} y={Y.socle} width={RIS * 2} height={-Y.socle} fill={t.stone} />

      {/* Rusticerad bottenvåning */}
      {range(7).map(i => (
        <path key={i} d={`M${L} ${Y.socle - 6 * (i + 1)} H${R}`} stroke={t.groove} strokeWidth="0.9" opacity="0.8" />
      ))}
      {/* Hörnkedjor vid mittpartiet */}
      {[L, -RIS, RIS - 7, R - 7].map(qx =>
        range(21).map(i => (
          <path key={`${qx}-${i}`} d={`M${qx} ${Y.band - 5 * (i + 1)} h7`} stroke={t.groove} strokeWidth="0.8" opacity="0.8" />
        ))
      )}

      {/* Gördelgesims, tandsnittsgesims och takfot */}
      {[
        [Y.band, 4],
        [Y.dentil, 8],
        [Y.eave, 4],
      ].map(([top, h]) => (
        <g key={top}>
          <rect x={L} y={top} width={R - L} height={h} fill={t.cornice} />
          <rect x={L} y={top} width={R - L} height="1" fill={t.trim} />
          <rect x={L} y={top + h} width={R - L} height="1.6" fill={t.shadow} opacity="0.55" />
          {winter && top !== Y.eave && <path d={snowCap(L - 1, R + 1, top - 0.6, 1.4, top)} fill={t.snow} opacity="0.95" />}
        </g>
      ))}
      {range(Math.floor((R - L) / 3.4)).map(i => (
        <rect key={i} x={L + 1 + i * 3.4} y={Y.dentil + 4} width="1.8" height="3" fill={t.shadow} opacity="0.55" />
      ))}

      {/* Himmelsljus uppifrån, varmt sken nerifrån från lyktor och porten */}
      <rect x={L} y={Y.eave} width={R - L} height={-Y.eave} fill={`url(#${id}-shade)`} />

      {/* Flyglarnas fönster */}
      {wingX.map((wx, i) => (
        <g key={wx}>
          <Win x={wx} top={Y.ground + 12} w={12} h={22} shape="arch" keystone lit={lit(0.25)} flicker={lit(0.5)} delay={-rnd() * 3} {...windowProps} />
          {[Y.f1, Y.f2, Y.f3].map((top, row) => {
            if (wx === WIDOW_X && row === 1 && !winter) {
              return (
                <Win key={top} x={wx} top={top + 5} w={11} h={18} shape="seg" lit {...windowProps}>
                  <Widow x={wx} top={top + 5} p={p} />
                </Win>
              );
            }
            const on = lit(0.3);
            return (
              <Win key={top} x={wx} top={top + 5} w={11} h={18} shape="seg" lit={on} flicker={lit(0.4) && !winter} delay={-rnd() * 3} {...windowProps}>
                {on && deco(wx, top + 5, 11, 18, i + row)}
              </Win>
            );
          })}
          <Win x={wx} top={Y.attic + 6} w={8} h={10} shape="arch" lit={lit(0.2)} {...windowProps} />
        </g>
      ))}

      {/* Mittpartiets höga rundbågar och vindsvåningens små bågar */}
      {[-34, 0, 34].map((mx, i) => {
        const lower = winter || mx === 0;
        const upper = winter ? mx !== 0 : mx === 34;
        return (
          <g key={mx}>
            <Win x={mx} top={Y.band - 36} w={20} h={32} shape="arch" oculus lit={lower} flicker={!winter} {...windowProps}>
              {winter && lower && <CandleBridge x={mx} y={Y.band - 4.6} w={14} />}
            </Win>
            <Win x={mx} top={Y.dentil + 12} w={20} h={32} shape="arch" oculus lit={upper} delay={-1.4} flicker={!winter} {...windowProps}>
              {winter && upper && <WindowStar x={mx} y={Y.dentil + 31} r={4} />}
            </Win>
          </g>
        );
      })}
      {range(7).map(i => (
        <Win key={i} x={-39 + i * 13} top={Y.attic + 5} w={8} h={11} shape="arch" lit={i === 2 || i === 5 || (winter && i === 3)} {...windowProps} />
      ))}

      {/* Porten med stenomfattning, överljus och ljus som strömmar ut */}
      <path d="M-24 0 H24 L12 -8 H-12 Z" fill={p[300]} opacity="0.35" filter="url(#toon-soft)" />
      <path d="M-14 -6 V-26 A14 14 0 0 1 14 -26 V-6 Z" fill={t.stone} />
      <path d="M-14 -26 A14 14 0 0 1 14 -26" stroke={t.trim} strokeWidth="1" fill="none" opacity="0.6" />
      <path d="M-3 -45 H3 L2 -38 H-2 Z" fill={t.stone} />
      <path d="M-3 -45 H3" stroke={t.trim} strokeWidth="0.8" opacity="0.6" />
      <path d="M-10 -6 V-26 A10 10 0 0 1 10 -26 V-6 Z" fill={`url(#${doorGrad})`} />
      <path d="M-10 -26 A10 10 0 0 1 10 -26 Z" fill={p[200]} opacity="0.7" />
      <path d="M-10 -26 H10 M0 -26 V-36 M-7 -33 L0 -26 L7 -33" stroke={t.wood} strokeWidth="1.1" />
      <text x="0" y="-28.2" textAnchor="middle" fontFamily="Georgia, serif" fontSize="4" fontWeight="700" fill={t.wood}>
        45
      </text>
      {/* Vänster dörrblad stängt, höger står på glänt */}
      <rect x="-10" y="-26" width="10" height="20" fill={t.wood} />
      <path d="M-8.5 -24 h7 v7 h-7 Z M-8.5 -15 h7 v7 h-7 Z" fill="none" stroke={mix("#b77a52", t.night, 0.4)} strokeWidth="0.8" />
      <path d="M10 -26 L 6.6 -24.8 V -7.2 L 10 -6 Z" fill={mix("#8a5230", t.night, 0.62)} />
      {winter && (
        <g>
          {/* Julkrans med röd rosett på det stängda dörrbladet */}
          <circle cx="-5" cy="-17" r="3.3" fill="none" stroke="#24563a" strokeWidth="2" />
          <circle cx="-5" cy="-17" r="3.3" fill="none" stroke="#3f8a4a" strokeWidth="0.8" strokeDasharray="1 1.4" />
          <path d="M-6.6 -13.8 L-5 -14.8 L-3.4 -13.8 L-3.8 -12 L-5 -13.6 L-6.2 -12 Z" fill="#d9243a" />
        </g>
      )}
      {/* Trappan */}
      <rect x="-22" y="-2" width="44" height="2" fill={t.stoneDark} />
      <rect x="-19" y="-4" width="38" height="2" fill={t.stone} />
      <rect x="-16" y="-6" width="32" height="2" fill={mix("#9a948d", t.night, 0.4)} />
      {winter && (
        <>
          <path d={snowCap(-22, -10, -2.6, 1, 3)} fill={t.snow} />
          <path d={snowCap(10, 22, -2.6, 1, 4)} fill={t.snow} />
        </>
      )}

      {/* Skylten över porten */}
      <rect x="-56" y={Y.band} width="112" height="11" fill={mix("#4a3e38", t.night, 0.3)} />
      <rect x="-56" y={Y.band} width="112" height="0.9" fill={t.trim} opacity="0.6" />
      <rect x="-56" y={Y.band + 11} width="112" height="1" fill={t.shadow} opacity="0.7" />
      <text
        x="0.3"
        y={Y.band + 8.6}
        textAnchor="middle"
        fontFamily="Georgia, 'Times New Roman', serif"
        fontSize="8"
        fontWeight="700"
        textLength="104"
        lengthAdjust="spacingAndGlyphs"
        fill="#0e0604"
        opacity="0.8"
      >
        BORGERSKAPETS ENKEHUS
      </text>
      <text
        x="0"
        y={Y.band + 8.2}
        textAnchor="middle"
        fontFamily="Georgia, 'Times New Roman', serif"
        fontSize="8"
        fontWeight="700"
        textLength="104"
        lengthAdjust="spacingAndGlyphs"
        fill={mix("#d9c6b4", p[200], 0.35)}
      >
        BORGERSKAPETS ENKEHUS
      </text>

      {/* Glaskupolerna på väggen bredvid porten */}
      {[-25, 25].map((lx, i) => (
        <g key={lx}>
          <circle cx={lx} cy="-30" r="11" fill={p[300]} opacity="0.3" filter="url(#toon-softer)" className="hol-breathe" style={{ animationDelay: `${-i * 1.7}s` }} />
          <path d={`M${lx} -23 V-26.5`} stroke={t.slate} strokeWidth="1.4" />
          <rect x={lx - 1.6} y="-24" width="3.2" height="2.4" fill={t.slate} />
          <circle cx={lx} cy="-30" r="3.8" fill={p[50]} />
          <circle cx={lx - 1.2} cy="-31.3" r="1.2" fill="#ffffff" />
        </g>
      ))}

      {/* Sockeln med årtalet och klocktornet */}
      <path d={`M-24 ${Y.eave} V${Y.eave - 8} L-16 ${Y.eave - 12} H16 L24 ${Y.eave - 8} V${Y.eave} Z`} fill={t.wallProud} />
      <path d={`M-24 ${Y.eave - 8} L-16 ${Y.eave - 12} H16 L24 ${Y.eave - 8}`} stroke={t.trim} strokeWidth="1" fill="none" />
      <text x="0" y={Y.eave - 3} textAnchor="middle" fontFamily="Georgia, serif" fontSize="6" fontWeight="700" fill={t.shadow} letterSpacing="0.6">
        1879
      </text>
      {winter && (
        <path d={`M-24.6 ${Y.eave - 8} L-16 ${Y.eave - 12.8} H16 L24.6 ${Y.eave - 8} L23 ${Y.eave - 7} Q14 ${Y.eave - 10.4} 0 ${Y.eave - 10.6} Q-14 ${Y.eave - 10.4} -23 ${Y.eave - 7} Z`} fill={t.snow} />
      )}
      <rect x="-16" y="-182" width="32" height="2" fill={t.slateLit} />
      <rect x="-15" y="-216" width="30" height="34" fill={t.slate} />
      <rect x="8" y="-216" width="7" height="34" fill={t.slateLit} opacity="0.55" />
      <path d="M-15 -216 L-20 -221 H20 L15 -216 Z" fill={t.slateLit} />
      <path d="M-18 -221 L0 -252 V-221 Z" fill={t.slate} />
      <path d="M0 -252 L18 -221 H0 Z" fill={t.slateLit} />
      {winter && (
        <>
          {/* Snö på tornets krage och i takfallens nederkant */}
          <path d={snowCap(-20.5, 20.5, -221.6, 1.6, 21)} fill={t.snow} />
          <path d="M-14 -226 Q-7 -229 0 -228 Q7 -229 14 -226 L16.6 -222.4 H-16.6 Z" fill={t.snow} opacity="0.92" />
          <path d="M0 -236 L4 -231 L0 -230 L-3 -232 Z" fill={t.snow} opacity="0.7" />
          <path d={snowCap(-16, 16, -182.8, 1.2, 22)} fill={t.snow} />
        </>
      )}
      <path d={`M0 -252 V${ENKEHUS_SPIRE_TOP}`} stroke={t.slateLit} strokeWidth="1.1" />
      <circle cx="0" cy="-257" r="1.6" fill={t.slateLit} />
      {/* Urtavlan lyser */}
      <circle cx="0" cy="-199" r="16" fill={p[100]} opacity="0.28" filter="url(#toon-softer)" className="hol-breathe" />
      <circle cx="0" cy="-199" r="10.5" fill={mix(p[50], "#ffffff", 0.3)} />
      <circle cx="0" cy="-199" r="10.5" fill="none" stroke={mix("#c9b8a8", t.night, 0.3)} strokeWidth="1" />
      {range(12).map(i => {
        const a = (i * 30 * Math.PI) / 180;
        const r1 = i % 3 === 0 ? 6.6 : 7.8;
        return (
          <path
            key={i}
            d={`M${Math.sin(a) * r1} ${-199 - Math.cos(a) * r1} L${Math.sin(a) * 9.2} ${-199 - Math.cos(a) * 9.2}`}
            stroke="#2a1a14"
            strokeWidth={i % 3 === 0 ? 1.2 : 0.7}
          />
        );
      })}
      <path d={hand(clock.hour, 5.2)} stroke="#1a0c06" strokeWidth="1.5" strokeLinecap="round" />
      <path d={hand(clock.minute, 8)} stroke="#1a0c06" strokeWidth="1" strokeLinecap="round" />
      <circle cx="0" cy="-199" r="1" fill="#1a0c06" />

      {winter ? (
        <>
          {/* Krukgranar med ljusslingor vid trappan och marschaller i snön */}
          <PottedSpruce x={-27} y={1} t={t} delay={0} />
          <PottedSpruce x={27} y={1} t={t} delay={-1.6} />
          <Marschall x={-44} y={10} delay={-0.4} />
          <Marschall x={44} y={10} delay={-1.9} />
          {/* Snövall längs trottoarkanten */}
          <path d={snowCap(L - 16, -52, 9, 3, 31)} fill={t.snow} />
          <path d={snowCap(52, R + 16, 9, 3, 37)} fill={t.snow} />
        </>
      ) : (
        <>
          {/* Smidesstaketet mot gatan, med grindstolpar vid porten */}
          <g fill={t.iron} stroke={t.iron}>
            {[
              [L - 14, -24],
              [24, R + 14],
            ].map(([a, b]) => (
              <g key={a}>
                <path d={`M${a} 10 H${b} M${a} -3 H${b}`} strokeWidth="1.1" fill="none" />
                {range(Math.floor((b - a) / 5) + 1).map(i => {
                  const fx = a + i * 5;
                  return <path key={i} d={`M${fx - 0.5} 11 V-7 L${fx} -9.4 L${fx + 0.5} -7 V11 Z`} stroke="none" />;
                })}
                {range(Math.floor((b - a) / 10)).map(i => (
                  <circle key={i} cx={a + 7.5 + i * 10} cy="3.5" r="2" fill="none" strokeWidth="0.7" />
                ))}
              </g>
            ))}
            {/* Grindarna står öppna inåt */}
            <path d="M-24 10 L-17 6 M-24 -3 L-17 -5.5" strokeWidth="1" fill="none" />
            <path d="M24 10 L17 6 M24 -3 L17 -5.5" strokeWidth="1" fill="none" />
          </g>
          {[-27, 27].map(gx => (
            <g key={gx}>
              <rect x={gx - 3.5} y="-12" width="7" height="24" fill={t.stoneDark} />
              <rect x={gx - 3.5} y="-12" width="3" height="24" fill={t.stone} opacity="0.5" />
              <rect x={gx - 4.5} y="-14" width="9" height="2.4" fill={t.stone} />
            </g>
          ))}
          {/* Lyktgubbar på grindstolparna, där krukgranarna står i verkligheten */}
          <Pumpkin x={-27} y={-13.6} s={0.17} p={p} face="happy" delay={-0.8} />
          <Pumpkin x={27} y={-13.6} s={0.17} p={p} face="spooky" delay={-2.1} />

          {/* Fladdermöss runt tornet */}
          <TowerBat path={towerBatPath} dur={7} begin={0} />
          <TowerBat path={towerBatPath} dur={7} begin={-2.6} s={0.8} />
          <TowerBat path={towerBatPath} dur={9} begin={-5} s={0.65} />
        </>
      )}
    </g>
  );
};

export default Enkehus;
